import { useMutation } from '@tanstack/react-query';

import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { queryClient, withError } from '@/react-tools/react-query';
import { EnvironmentId } from '@/react/portainer/environments/types';

import { TRIVY_API, TRIVY_LABELS } from '../types';

import { queryKeys } from './queryKeys';

/**
 * Deletes a workload's vulnerability reports, which is what makes the scanner
 * look at it again: trivy-operator owns the reports, so removing one puts the
 * workload back in its queue. Reports are otherwise only refreshed when their
 * TTL expires - the policy's rescan interval, which can be weeks - so a policy
 * change such as a narrowed severity list does not reach existing findings
 * until an admin asks for this.
 */
export function useRescanWorkload(environmentId: EnvironmentId) {
  return useMutation(
    ({ namespace, workload }: { namespace: string; workload: string }) =>
      deleteWorkloadReports(environmentId, namespace, workload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.base(environmentId));
      },
      ...withError('Unable to request a rescan'),
    }
  );
}

/**
 * One report exists per container, so the whole workload is addressed by label
 * rather than by the derived report names.
 */
async function deleteWorkloadReports(
  environmentId: EnvironmentId,
  namespace: string,
  workload: string
) {
  try {
    await axios.delete(
      `/endpoints/${environmentId}/kubernetes/${TRIVY_API}/namespaces/${namespace}/vulnerabilityreports`,
      { params: { labelSelector: `${TRIVY_LABELS.resourceName}=${workload}` } }
    );
  } catch (error) {
    throw parseAxiosError(
      error as Error,
      `Failed to delete the vulnerability reports for ${namespace}/${workload}`
    );
  }
}
